import * as React from "react"
import { Route, Switch, useRouteMatch } from "react-router-dom"
import { type DigitsRoute } from "@digits-shared/components/Router/DigitsRoute"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { FullScreenScrollableContainer } from "src/frontend/components/OS/Applications/Shared/FullScreenScrollableContainer"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
 STYLES
*/

export const PageContainer = styled.div`
  min-height: 120px;
  width: 100%;
  background: ${colors.cloudWhite};
  border: 1px solid ${colors.white};
  border-radius: 16px;
  padding: 16px;
  &.no-padding {
    padding: 0;
  }
`

/*
 INTERFACES
*/

interface ChromeProps {
  Header: React.FC
  fallThrough: DigitsRoute
}

/*
 COMPONENTS
*/

export const AnalysisChrome: React.FC<React.PropsWithChildren<ChromeProps>> = ({
  Header,
  children,
  fallThrough,
}) => {
  const session = useSession<FrontendSession>()
  const intervalOrigin = useIntervalOrigin()
  const intervalOriginNoCount = { ...intervalOrigin }
  delete intervalOriginNoCount.intervalCount

  const redirectParams = { leSlug: session.currentLegalEntity.slug, ...intervalOriginNoCount }
  const revenueTransactions = !!useRouteMatch(routes.revenueTransactions.parameterizedPath)
  const expensesTransactions = !!useRouteMatch(routes.expenseTransactions.parameterizedPath)
  const className = stringHelper.classNames(
    (revenueTransactions || expensesTransactions) && "no-padding"
  )

  return (
    <>
      <Header />
      <FullScreenScrollableContainer fullWidth>
        <PageContainer className={className}>
          <React.Suspense fallback={<div />}>
            <Switch>
              {children}

              {/* Catch any fall through routes and redirect accordingly */}
              <Route
                path={fallThrough.parameterizedPath}
                render={() => (
                  <LoggedRedirect
                    name="AnalysisChrome-FallThrough"
                    to={fallThrough.generate(redirectParams)}
                  />
                )}
              />

              <Route
                render={() => (
                  <LoggedRedirect
                    name="AnalysisChrome-rootFallThrough"
                    to={routes.root.generate()}
                  />
                )}
              />
            </Switch>
          </React.Suspense>
        </PageContainer>
      </FullScreenScrollableContainer>
    </>
  )
}
